import { AiToolCallState, TreadAgentApiWriteType } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { startCase } from 'lodash-es';

import {
  TreadAgentConversationListing,
  TreadAgentSkillBasic,
} from '~hooks/useTreadAgent/models';
import { TreadAgentToolCallPreview, TreadAgentToolCallState } from '~tread-agent/types';

// Self-registered so relative timestamps and tz-aware recency grouping work regardless of
// import order (same self-registration pattern as dateRangePersistence.ts).
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// Compact "2 h" / "3 d" / "1 mo" relative label (suffix suppressed), tz-applied at display
// time rather than at storage time. Used for conversation timestamps in the dock history and expanded view.
export function formatRelativeShort(date: Dayjs) {
  return dayjs.tz(date).fromNow(true);
}

// Suggested prompts offered in the panel's fresh-chat empty state.
export const TREAD_AGENT_STARTER_PROMPT_KEYS = [
  'tread_agent.starter_prompts.schedule_orders',
  'tread_agent.starter_prompts.update_contact_information',
  'tread_agent.starter_prompts.recap_dispatch_activity',
  'tread_agent.starter_prompts.summarize_order_loads',
  'tread_agent.starter_prompts.set_up_rate_cards',
];

// Tool-call states that no longer need user input (the backend also stops returning them
// among a conversation's pending_tool_calls).
const TERMINAL_TOOL_CALL_STATES: TreadAgentToolCallState[] = [
  AiToolCallState.APPLIED,
  AiToolCallState.CANCELED,
  AiToolCallState.EXPIRED,
  AiToolCallState.FAILED,
];

export function isTerminalToolCallState(state: TreadAgentToolCallState) {
  return TERMINAL_TOOL_CALL_STATES.includes(state);
}

// Backend tool names arrive as snake_case identifiers (e.g. "search_orders").
// Humanize them for display in activity lines and confirmation cards.
export function humanizeTreadAgentToolName(toolName: string) {
  return toolName.replace(/_/g, ' ');
}

export function confirmationTitle(
  preview: TreadAgentToolCallPreview | null,
  toolName: string,
) {
  const actionFromToolName = startCase(humanizeTreadAgentToolName(toolName));

  if (preview?.kind === TreadAgentApiWriteType.API_WRITE) {
    // Untranslated because the server builds this phrase.
    return preview.title || actionFromToolName;
  }

  return actionFromToolName;
}

export function sortConversationsByMostRecentActivity(
  conversations: TreadAgentConversationListing[],
) {
  return conversations
    .slice()
    .sort(
      (a, b) =>
        (b.lastActivityAt ?? b.createdAt).valueOf() -
        (a.lastActivityAt ?? a.createdAt).valueOf(),
    );
}

export function filterConversationsByTitle(
  conversations: TreadAgentConversationListing[],
  search: string,
) {
  const query = search.trim().toLowerCase();

  if (!query) {
    return conversations;
  }

  return conversations.filter((conversation) =>
    (conversation.title ?? '').toLowerCase().includes(query),
  );
}

export function filterTreadAgentSkillsBySearch(
  skills: TreadAgentSkillBasic[],
  search: string,
) {
  const query = search.trim().toLowerCase();

  if (!query) {
    return skills;
  }

  return skills.filter(
    (skill) =>
      skill.name.toLowerCase().includes(query) ||
      skill.description.toLowerCase().includes(query),
  );
}
